import { stripFences } from './code';
import type { Replay } from './recording';
import type { FinalReport, StreamEvent } from './stream';

export interface Generation {
  prompt: string;
  code: string;
}

export type RunPhase =
  | 'loaded'
  | 'requesting'
  | 'streaming'
  | 'compiling'
  | 'rendered'
  | 'render-failed'
  | 'render-stalled'
  | 'incomplete'
  | 'request-failed'
  | 'stream-failed';

/** What the bundler reported for a failed compile, split into the first line and the code frame. */
export interface BundlerError {
  headline: string;
  frame: string;
  line: number | null;
}

/** A failure that ended a run without a render: the request, the stream or the bundler. */
export interface RunFailure {
  label: string;
  code: string;
  message: string;
}

/**
 * The bundler never reported a compile. `timeout`: Sandpack gave up connecting to it.
 * `stalled`: a compile sent to it had no answer after `waited` ms.
 */
export interface BundlerSilence {
  reason: 'timeout' | 'stalled';
  waited: number;
}

/**
 * One generation, live or replayed. Every time is milliseconds after `startedAt`
 * on the client clock, taken when the client observed the event.
 */
export interface Run {
  id: number;
  replay: Replay | null;
  prompt: string;
  startedAt: number;
  phase: RunPhase;
  respondedAt: number | null;
  texts: ReadonlyArray<{ t: number; text: string }>;
  raw: string;
  code: string;
  final: FinalReport | null;
  streamEndedAt: number | null;
  settledAt: number | null;
  renderOk: boolean | null;
  compileError: BundlerError | null;
  silence: BundlerSilence | null;
  failure: RunFailure | null;
  prior: Generation | null;
}

export interface State {
  prompt: string;
  run: Run | null;
  generation: Generation | null;
  lastGood: Generation | null;
  error: string | null;
  scrub: number | null;
}

export type Action =
  | { type: 'setPrompt'; prompt: string }
  | { type: 'blocked'; error: string }
  | { type: 'start'; id: number; prompt: string; replay: Replay | null; startedAt: number }
  | { type: 'responded'; id: number; at: number }
  | { type: 'event'; id: number; at: number; event: StreamEvent }
  | { type: 'fail'; id: number; at: number; failure: RunFailure }
  | { type: 'compiled'; code: string; ok: boolean; swapped: boolean; at: number }
  | { type: 'compileError'; code: string; error: BundlerError }
  | { type: 'bundlerSilent'; code: string; silence: BundlerSilence; at: number }
  | { type: 'bundlerRetry' }
  | { type: 'scrub'; t: number | null };

export const firstTokenAt = (run: Run): number | null => run.texts[0]?.t ?? null;

/** The moment the run stopped changing: rendered or failed, or the stream end when nothing was compiled. */
export const runEnd = (run: Run): number | null => run.settledAt ?? run.streamEndedAt;

export const isBusy = (run: Run | null): boolean => run !== null && (run.phase === 'requesting' || run.phase === 'streaming');

/** The source as it stood `t` ms into the run, from the text events received by then. */
export function codeAt(run: Run, t: number): string {
  return stripFences(run.texts.filter((e) => e.t <= t).map((e) => e.text).join(''));
}

/** The opening state for a recording shown at rest: its finished output, with the recorded timings. */
export function loadedRun(replay: Replay): Run {
  const texts = replay.events.flatMap(({ t, event }) => (event.kind === 'text' ? [{ t, text: event.text }] : []));
  const raw = texts.map((e) => e.text).join('');
  return {
    id: 0,
    replay,
    prompt: replay.prompt,
    startedAt: 0,
    phase: 'loaded',
    respondedAt: null,
    texts,
    raw,
    code: replay.code,
    final: replay.final,
    streamEndedAt: replay.events[replay.events.length - 1].t,
    settledAt: null,
    renderOk: null,
    compileError: null,
    silence: null,
    failure: null,
    prior: null,
  };
}

export function initialState(opening: Replay | null): State {
  return {
    prompt: '',
    run: opening === null ? null : loadedRun(opening),
    generation: opening === null ? null : { prompt: opening.prompt, code: opening.code },
    lastGood: null,
    error: null,
    scrub: null,
  };
}

function stoppedMessage(stop: string | null): string {
  if (stop === 'refusal') return 'Claude declined this prompt (stop_reason "refusal"), so nothing was compiled.';
  if (stop === null) return 'The API reported no stop_reason for this message, so nothing was compiled.';
  return `Claude stopped with stop_reason "${stop}" before finishing the component, so nothing was compiled.`;
}

function silenceFailure(silence: BundlerSilence): RunFailure {
  if (silence.reason === 'timeout') {
    return { label: 'NO RENDER', code: 'bundler timeout', message: 'Sandpack could not connect to the CodeSandbox bundler and gave up.' };
  }
  return {
    label: 'NO RENDER',
    code: 'bundler stalled',
    message: `The CodeSandbox bundler reported no compile after ${(silence.waited / 1000).toFixed(1)} s.`,
  };
}

function applyEvent(state: State, run: Run, at: number, event: StreamEvent): State {
  switch (event.kind) {
    case 'text': {
      const raw = run.raw + event.text;
      return { ...state, run: { ...run, phase: 'streaming', texts: [...run.texts, { t: at, text: event.text }], raw, code: stripFences(raw) } };
    }
    case 'final':
      return { ...state, run: { ...run, final: event.report } };
    case 'error': {
      const failure = { label: 'STREAM FAILED', code: event.code, message: event.error };
      return { ...state, error: event.error, run: { ...run, phase: 'stream-failed', settledAt: at, failure } };
    }
    case 'done': {
      const ended = { ...run, streamEndedAt: at };
      const stop = run.final?.stop_reason ?? null;
      if (stop === 'max_tokens') {
        return { ...state, run: { ...ended, phase: 'incomplete', settledAt: at } };
      }
      if (stop !== 'end_turn') {
        const message = stoppedMessage(stop);
        const failure = { label: 'STOPPED', code: stop ?? '—', message };
        return { ...state, error: message, run: { ...ended, phase: 'stream-failed', settledAt: at, failure } };
      }
      if (run.code.trim() === '') {
        const message = 'Claude finished without returning any code.';
        const failure = { label: 'NO CODE', code: stop, message };
        return { ...state, error: message, run: { ...ended, phase: 'stream-failed', settledAt: at, failure } };
      }
      return { ...state, generation: { prompt: run.prompt, code: run.code }, run: { ...ended, phase: 'compiling' } };
    }
  }
}

export function reducer(state: State, action: Action): State {
  const run = state.run;
  switch (action.type) {
    case 'setPrompt':
      return { ...state, prompt: action.prompt };
    case 'blocked':
      return { ...state, error: action.error };
    case 'start':
      return {
        ...state,
        error: null,
        scrub: null,
        run: {
          id: action.id,
          replay: action.replay,
          prompt: action.prompt,
          startedAt: action.startedAt,
          phase: action.replay === null ? 'requesting' : 'streaming',
          respondedAt: null,
          texts: [],
          raw: '',
          code: '',
          final: null,
          streamEndedAt: null,
          settledAt: null,
          renderOk: null,
          compileError: null,
          silence: null,
          failure: null,
          prior: state.lastGood,
        },
      };
    case 'responded':
      if (run === null || run.id !== action.id || run.phase !== 'requesting') return state;
      return { ...state, run: { ...run, phase: 'streaming', respondedAt: action.at } };
    case 'event':
      if (run === null || run.id !== action.id || !isBusy(run)) return state;
      return applyEvent(state, run, action.at, action.event);
    case 'fail': {
      if (run === null || run.id !== action.id || !isBusy(run)) return state;
      const phase = run.phase === 'requesting' ? 'request-failed' : 'stream-failed';
      return { ...state, error: action.failure.message, run: { ...run, phase, settledAt: action.at, failure: action.failure } };
    }
    case 'compiled': {
      const lastGood =
        action.ok && !action.swapped && state.generation !== null
          ? { prompt: state.generation.prompt, code: action.code }
          : state.lastGood;
      if (run === null || run.code !== action.code || action.swapped) return { ...state, lastGood };
      if (run.phase === 'loaded') return { ...state, lastGood, run: { ...run, renderOk: action.ok, silence: null } };
      if (run.phase !== 'compiling' && run.phase !== 'render-stalled') return { ...state, lastGood };
      return {
        ...state,
        lastGood,
        run: {
          ...run,
          phase: action.ok ? 'rendered' : 'render-failed',
          renderOk: action.ok,
          settledAt: action.at - run.startedAt,
          silence: null,
          failure: null,
        },
      };
    }
    case 'compileError': {
      const lastGood = state.lastGood?.code === action.code ? (run?.prior ?? null) : state.lastGood;
      if (run === null || run.code !== action.code) return { ...state, lastGood };
      const compileError = run.compileError ?? action.error;
      switch (run.phase) {
        case 'loaded':
          return { ...state, lastGood, run: { ...run, renderOk: false, compileError } };
        case 'compiling':
        case 'render-failed':
          return { ...state, lastGood, run: { ...run, compileError } };
        case 'rendered':
          return { ...state, lastGood, run: { ...run, phase: 'render-failed', renderOk: false, compileError } };
        default:
          return { ...state, lastGood };
      }
    }
    case 'bundlerSilent': {
      if (run === null || run.code !== action.code) return state;
      if (run.phase === 'loaded') return { ...state, run: { ...run, silence: action.silence } };
      if (run.phase !== 'compiling') return state;
      return {
        ...state,
        run: {
          ...run,
          phase: 'render-stalled',
          silence: action.silence,
          settledAt: action.at - run.startedAt,
          failure: silenceFailure(action.silence),
        },
      };
    }
    case 'bundlerRetry':
      if (run === null || run.silence === null) return state;
      if (run.phase === 'render-stalled') return { ...state, run: { ...run, phase: 'compiling', silence: null, settledAt: null, failure: null } };
      return { ...state, run: { ...run, silence: null } };
    case 'scrub':
      return { ...state, scrub: action.t };
  }
}
